import { openModal, ProductsByCategoriesQuery } from "@chef/state-management";
import {
  getPriceOfProductByVariation,
  getStockData,
  getBasketProductQuantity,
} from "@chef/state-management/helpers";
import {
  useAppDispatch,
  useStandaloneBasket,
} from "@chef/state-management/hooks";
import { Switch } from "@chef/components";
import { isEqualStrings } from "@chef/utils/equal";

import { intl } from "./StandaloneProductTile.Intl";

type SubscriptionFooterProps = {
  variation: ProductsByCategoriesQuery["productsByCategories"][number]["products"][number]["variations"][number];
  basketType: "billing" | "deviation";
  week: number;
  year: number;
};

export const SubscriptionFooter = (props: SubscriptionFooterProps) => {
  const { variation, basketType, week, year } = props;
  const dispatch = useAppDispatch();

  const {
    basket,
    addProductToSubscription,
    subscribedProducts,
    isBillingLoading,
  } = useStandaloneBasket({
    variant: basketType,
    week,
    year,
  });

  const productId = variation.product.productId;
  const productTypeId = variation.product.productTypeId;
  const variationId = variation.variationId;
  const name = variation.name;
  const price = getPriceOfProductByVariation(variation);

  const basketQuantity = getBasketProductQuantity(
    basket,
    productId,
    variationId,
  );

  const { maxPurchasableAmount } = getStockData({
    variation,
    addedQuantity: basketQuantity,
  });

  const subscribedQuantity =
    subscribedProducts?.find((p) =>
      isEqualStrings(p.variation.variationId, variation.variationId),
    )?.quantity ?? 0;

  const onChangeSubscribed = () =>
    dispatch(
      openModal({
        id: "SUBSCRIBED_STANDALONE",
        deviationProduct: {
          productId,
          productTypeId,
          quantity: subscribedQuantity || 0,
          variationId,
          price,
        },
        productName: name,
        maxQtyAllowed: maxPurchasableAmount,
        week,
        year,
      }),
    );

  const onAddToSubscription = () => {
    addProductToSubscription({
      productId,
      productTypeId,
      quantity: subscribedQuantity ? 0 : basketQuantity,
      variationId,
      price,
    });
  };

  if (!basketQuantity) {
    return;
  }

  return (
    <div
      id="add-to-subscription"
      className="relative flex justify-between w-full h-10 p-2 text-sm bg-white rounded-b-lg shadow-lg"
    >
      <div className="flex justify-between text-sm">
        {subscribedQuantity ? (
          <div className="flex items-start gap-1">
            <span>{`${subscribedQuantity} ${intl.PIECES}:`}</span>
            <button
              className="underline capitalize"
              onClick={onChangeSubscribed}
            >
              {intl.CHANGE}
            </button>
          </div>
        ) : (
          <span>{intl.SUBSCRIBE}</span>
        )}
      </div>

      <Switch
        className="shrink-0"
        small
        checked={!!subscribedQuantity}
        onChange={onAddToSubscription}
        isLoading={isBillingLoading}
      />
    </div>
  );
};
